import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  channelTypeConfigs,
  channelContent,
  canalRFWStats,
  canalRFWRules,
  canalLocations,
  getContentByChannel,
  getFeaturedContent,
  getLiveContent,
  getContentTypeDistribution,
  getChannelDistribution,
} from '@/mocks/rfwCanalRFW';

type SidebarView = 'todos' | 'radio' | 'tv' | 'podcast' | 'noticias' | 'tendencias' | 'ubicaciones' | 'reglas';

export default function CanalRFWPage() {
  const [activeView, setActiveView] = useState<SidebarView>('todos');
  const [sortBy, setSortBy] = useState<'recientes' | 'populares' | 'destacados'>('destacados');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const featured = getFeaturedContent();
  const live = getLiveContent();
  const channelDist = getChannelDistribution();

  function getViewContent(): typeof channelContent {
    if (activeView === 'todos') return channelContent;
    const channelMap: Record<string, string> = {
      radio: 'rfw-radio',
      tv: 'rfw-tv',
      podcast: 'rfw-podcast',
      noticias: 'rfw-noticias',
      tendencias: 'rfw-tendencias',
    };
    const channelId = channelMap[activeView];
    if (channelId) return getContentByChannel(channelId);
    return channelContent;
  }

  function getSortedContent() {
    const content = getViewContent();
    if (sortBy === 'recientes') return [...content].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
    if (sortBy === 'populares') return [...content].sort((a, b) => b.playsCount - a.playsCount);
    return [...content].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || b.playsCount - a.playsCount);
  }

  const sortedContent = getSortedContent();

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Ahora';
    if (mins < 60) return `Hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Hace ${hours}h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days}d`;
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-rose-500 via-violet-500 to-amber-500 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle at 25px 25px, white 1px, transparent 0)',
              backgroundSize: '50px 50px',
            }}
          ></div>

          <div className="relative max-w-6xl mx-auto px-4 md:px-6 pt-20 pb-12 md:pt-24 md:pb-16">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-white/60 text-xs mb-4">
              <Link to="/" className="hover:text-white/80 transition-colors">Inicio</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white/80">Canal RFW</span>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-4">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
                <span className="text-xs font-semibold text-white/90 tracking-wide">CONTENIDO OFICIAL</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
                Canal <span className="text-amber-300">RFW</span>
              </h1>
              <p className="text-white/70 text-sm md:text-base max-w-lg mx-auto">
                Contenido original producido por RadioFrecuenciaWeb. Música, TV, podcasts, noticias y tendencias.
                Siempre hay algo para escuchar, ver o descubrir.
              </p>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mt-8">
              {[
                { value: canalRFWStats.totalContent.toString(), label: 'Contenidos', icon: 'ri-stack-line' },
                { value: canalRFWStats.channelsActive.toString(), label: 'Canales Activos', icon: 'ri-radio-line' },
                { value: canalRFWStats.liveNow.toString(), label: 'En Vivo', icon: 'ri-live-line' },
                { value: canalRFWStats.exclusiveContent.toString(), label: 'Exclusivos', icon: 'ri-star-line' },
                { value: canalRFWStats.totalPlays.toLocaleString(), label: 'Reproducciones', icon: 'ri-play-circle-line' },
              ].map((m, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center border border-white/10">
                  <div className="text-xl font-bold text-white">{m.value}</div>
                  <div className="flex items-center justify-center gap-1 text-xs text-white/60 mt-1">
                    <i className={`${m.icon} text-[10px]`}></i>
                    <span>{m.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Live Pulse Bar */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {[
                { label: 'En Vivo', value: live.length, icon: 'ri-live-line', color: 'text-rose-500', pulse: true },
                { label: 'Destacados', value: featured.length, icon: 'ri-star-line', color: 'text-amber-500' },
                { label: 'Exclusivos', value: canalRFWStats.exclusiveContent, icon: 'ri-shield-star-line', color: 'text-emerald-500' },
                { label: 'RFW Radio', value: getContentByChannel('rfw-radio').length, icon: 'ri-radio-line', color: 'text-rose-500' },
                { label: 'RFW TV', value: getContentByChannel('rfw-tv').length, icon: 'ri-tv-line', color: 'text-violet-500' },
                { label: 'Actualización', value: canalRFWStats.updatedEvery, icon: 'ri-refresh-line', color: 'text-orange-500' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {item.pulse ? (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                      </span>
                    ) : (
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                    )}
                    <span className={`text-xs font-semibold ${item.color}`}>{item.value}</span>
                  </div>
                  <span className="text-[10px] text-foreground-400 hidden md:inline">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content Area */}
        <section className="py-8 md:py-12">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Sidebar */}
              <aside className="lg:w-64 flex-shrink-0">
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-4 lg:sticky lg:top-24">
                  <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Navegar</div>

                  {/* Main views */}
                  <div className="space-y-1 mb-5">
                    {([
                      { id: 'todos', label: 'Todos los Canales', icon: 'ri-grid-line', count: channelContent.length },
                      { id: 'radio', label: 'RFW Radio', icon: 'ri-radio-line', count: getContentByChannel('rfw-radio').length, color: 'rose' },
                      { id: 'tv', label: 'RFW TV', icon: 'ri-tv-line', count: getContentByChannel('rfw-tv').length, color: 'violet' },
                      { id: 'podcast', label: 'RFW Podcast', icon: 'ri-headphone-line', count: getContentByChannel('rfw-podcast').length, color: 'amber' },
                      { id: 'noticias', label: 'RFW Noticias', icon: 'ri-newspaper-line', count: getContentByChannel('rfw-noticias').length, color: 'emerald' },
                      { id: 'tendencias', label: 'RFW Tendencias', icon: 'ri-fire-line', count: getContentByChannel('rfw-tendencias').length, color: 'orange' },
                    ] as { id: SidebarView; label: string; icon: string; count: number; color?: string }[]).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => { setActiveView(item.id); setExpandedItem(null); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all text-left cursor-pointer whitespace-nowrap ${
                          activeView === item.id
                            ? 'bg-rose-500 text-white'
                            : 'text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        <i className={`${item.icon} text-sm`}></i>
                        <span className="flex-1">{item.label}</span>
                        <span className={`text-xs ${activeView === item.id ? 'text-white/70' : 'text-foreground-400'}`}>{item.count}</span>
                      </button>
                    ))}
                  </div>

                  <div className="border-t border-background-200/70 pt-4 mb-4">
                    <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Más</div>
                    <div className="space-y-1">
                      {([
                        { id: 'ubicaciones', label: 'Ubicaciones', icon: 'ri-map-pin-line' },
                        { id: 'reglas', label: 'Reglas Globales', icon: 'ri-scales-line' },
                      ] as { id: SidebarView; label: string; icon: string }[]).map((item) => (
                        <button
                          key={item.id}
                          onClick={() => { setActiveView(item.id); setExpandedItem(null); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all text-left cursor-pointer whitespace-nowrap ${
                            activeView === item.id
                              ? 'bg-rose-500 text-white'
                              : 'text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          <i className={`${item.icon} text-sm`}></i>
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Channel quick distribution */}
                  <div className="border-t border-background-200/70 pt-4">
                    <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Distribución</div>
                    <div className="space-y-1.5">
                      {channelDist.map((item) => (
                        <div key={item.channelId} className="flex items-center gap-2">
                          <i className={`${item.config?.icon ?? 'ri-radio-line'} text-xs ${item.config?.color === 'rose' ? 'text-rose-500' : item.config?.color === 'violet' ? 'text-violet-500' : item.config?.color === 'amber' ? 'text-amber-500' : item.config?.color === 'emerald' ? 'text-emerald-500' : 'text-orange-500'}`}></i>
                          <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${item.config?.gradient ?? 'from-gray-400 to-gray-500'}`}
                              style={{ width: `${(item.count / channelContent.length) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-[10px] text-foreground-400 w-5 text-right">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>

              {/* Content Area */}
              <div className="flex-1 min-w-0">
                {/* Sort bar - only show for content views */}
                {!['ubicaciones', 'reglas'].includes(activeView) && (
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-xs text-foreground-500">Ordenar:</span>
                    <div className="flex bg-background-100 rounded-full p-0.5 border border-background-200/70">
                      {(['destacados', 'populares', 'recientes'] as const).map((s) => (
                        <button
                          key={s}
                          onClick={() => setSortBy(s)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                            sortBy === s
                              ? 'bg-rose-500 text-white'
                              : 'text-foreground-500 hover:text-foreground-700'
                          }`}
                        >
                          {s === 'destacados' && 'Destacados'}
                          {s === 'populares' && 'Más Populares'}
                          {s === 'recientes' && 'Más Recientes'}
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-foreground-400 ml-auto">{sortedContent.length} contenidos</span>
                  </div>
                )}

                {/* VIEW: Content Grid (todos, radio, tv, podcast, noticias, tendencias) */}
                {!['ubicaciones', 'reglas'].includes(activeView) && (
                  <div>
                    {/* Featured banner for 'todos' and 'destacados' */}
                    {activeView === 'todos' && sortBy === 'destacados' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {featured.slice(0, 2).map((item) => {
                          const ch = channelTypeConfigs.find((c) => c.id === item.channelId);
                          return (
                            <div key={item.id} className="relative rounded-xl overflow-hidden group cursor-pointer h-56">
                              <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                              <div className="absolute bottom-0 left-0 right-0 p-5">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r ${ch?.gradient ?? 'from-gray-400 to-gray-500'} text-white`}>
                                    {item.type}
                                  </span>
                                  {item.isLive && (
                                    <span className="flex items-center gap-1 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                      EN VIVO
                                    </span>
                                  )}
                                </div>
                                <div className="text-white font-bold text-lg leading-snug mb-1">{item.title}</div>
                                <div className="text-white/70 text-xs">{item.subtitle}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Content grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sortedContent.map((item) => {
                        const ch = channelTypeConfigs.find((c) => c.id === item.channelId);
                        const isExpanded = expandedItem === item.id;
                        return (
                          <div
                            key={item.id}
                            className={`bg-background-100 rounded-xl border border-background-200/70 overflow-hidden transition-all ${
                              isExpanded ? 'md:col-span-2 lg:col-span-3' : 'hover:border-rose-200'
                            }`}
                          >
                            {/* Thumbnail + overlay */}
                            <div
                              className="relative h-44 overflow-hidden cursor-pointer"
                              onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                            >
                              <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                              {/* Badges */}
                              <div className="absolute top-2 left-2 flex items-center gap-1.5">
                                {item.isLive && (
                                  <span className="flex items-center gap-1 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                    EN VIVO
                                  </span>
                                )}
                                {item.isExclusive && !item.isLive && (
                                  <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    EXCLUSIVO
                                  </span>
                                )}
                                {item.isSponsored && (
                                  <span className="bg-foreground-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    PATROCINADO
                                  </span>
                                )}
                              </div>

                              {/* Channel badge */}
                              <div className="absolute top-2 right-2">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r ${ch?.gradient ?? 'from-gray-400 to-gray-500'} text-white`}>
                                  {ch?.shortLabel ?? item.channelId}
                                </span>
                              </div>

                              {/* Duration */}
                              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                                {item.duration}
                              </div>

                              {/* Play overlay */}
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                                  <i className="ri-play-fill text-rose-500 text-xl ml-0.5"></i>
                                </div>
                              </div>
                            </div>

                            {/* Info */}
                            <div className="p-4">
                              <div className="flex items-center gap-1.5 mb-2">
                                <span className="text-[10px] font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">{item.type}</span>
                                <span className="text-[10px] text-foreground-400">{item.category}</span>
                              </div>
                              <div
                                className="font-semibold text-sm text-foreground-950 leading-snug mb-2 cursor-pointer hover:text-rose-600 transition-colors"
                                onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                              >
                                {item.title}
                              </div>
                              <p className={`text-xs text-foreground-600 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'} mb-3`}>
                                {item.description}
                              </p>

                              {/* Metadata */}
                              <div className="flex items-center gap-2 text-[11px] text-foreground-400 mb-3">
                                <span className="flex items-center gap-0.5">
                                  <i className="ri-play-circle-line"></i>
                                  {item.playsCount.toLocaleString()}
                                </span>
                                <span className="flex items-center gap-0.5">
                                  <i className="ri-heart-line"></i>
                                  {item.likesCount.toLocaleString()}
                                </span>
                                <span className="flex items-center gap-0.5">
                                  <i className="ri-chat-3-line"></i>
                                  {item.commentsCount}
                                </span>
                                <span className="flex items-center gap-0.5 ml-auto">
                                  <i className="ri-time-line text-[10px]"></i>
                                  {timeAgo(item.publishDate)}
                                </span>
                              </div>

                              {/* Tags */}
                              {isExpanded && (
                                <div className="flex flex-wrap gap-1 mb-3">
                                  {item.tags.map((tag) => (
                                    <span key={tag} className="text-[10px] text-foreground-500 bg-background-200/50 px-2 py-0.5 rounded-full">{tag}</span>
                                  ))}
                                </div>
                              )}

                              {/* Source info */}
                              {item.sourceRadio && (
                                <div className={`${isExpanded ? '' : 'hidden'} text-[10px] text-foreground-400 mb-3`}>
                                  Fuente: {item.sourceRadio}{item.sourceHost ? ` · ${item.sourceHost}` : ''}
                                </div>
                              )}

                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-500 text-white text-xs font-semibold hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap">
                                  <i className="ri-play-fill"></i>
                                  Reproducir
                                </button>
                                <button className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center hover:bg-rose-100 hover:text-rose-500 transition-colors cursor-pointer">
                                  <i className="ri-heart-line text-sm"></i>
                                </button>
                                <button className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center hover:bg-rose-100 hover:text-rose-500 transition-colors cursor-pointer">
                                  <i className="ri-share-forward-line text-sm"></i>
                                </button>
                                <button
                                  onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                                  className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center ml-auto hover:bg-rose-100 hover:text-rose-500 transition-colors cursor-pointer"
                                >
                                  <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {sortedContent.length === 0 && (
                      <div className="text-center py-16">
                        <div className="w-20 h-20 rounded-full bg-background-200/50 flex items-center justify-center mx-auto mb-4">
                          <i className="ri-play-list-line text-3xl text-foreground-400"></i>
                        </div>
                        <p className="text-sm text-foreground-500">No hay contenido en esta vista ahora mismo.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* VIEW: Ubicaciones */}
                {activeView === 'ubicaciones' && (
                  <div>
                    <div className="mb-5">
                      <h3 className="text-lg font-bold text-foreground-950 mb-2">Dónde aparece Canal RFW</h3>
                      <p className="text-sm text-foreground-500">El contenido oficial de RFW se integra en 6 ubicaciones estratégicas para garantizar que nunca haya espacios vacíos.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {canalLocations.map((loc) => {
                        const primaryCh = channelTypeConfigs.find((c) => c.id === loc.primaryChannel);
                        return (
                          <Link
                            key={loc.id}
                            to={loc.path}
                            className="bg-background-100 rounded-xl border border-background-200/70 p-5 hover:border-rose-200 transition-all cursor-pointer group"
                          >
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${primaryCh?.gradient ?? 'from-gray-400 to-gray-500'}`}>
                                <i className={`${loc.icon} text-lg text-white`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-foreground-950 group-hover:text-rose-600 transition-colors mb-1">{loc.label}</div>
                                {primaryCh && (
                                  <div className={`text-[10px] font-semibold ${primaryCh.textColor} mb-2`}>
                                    Canal principal: {primaryCh.label}
                                  </div>
                                )}
                                <p className="text-xs text-foreground-600 leading-relaxed mb-3">{loc.description}</p>
                                <div className="flex items-center gap-1 text-[10px] text-foreground-400">
                                  <i className="ri-link"></i>
                                  <span>{loc.path}</span>
                                </div>
                              </div>
                              <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <i className="ri-arrow-right-s-line text-foreground-500"></i>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* VIEW: Reglas */}
                {activeView === 'reglas' && (
                  <div>
                    <div className="mb-5">
                      <h3 className="text-lg font-bold text-foreground-950 mb-2">Reglas Globales del Canal RFW</h3>
                      <p className="text-sm text-foreground-500">Tres reglas que garantizan que la plataforma siempre tenga contenido disponible.</p>
                    </div>
                    <div className="space-y-4 mb-8">
                      {canalRFWRules.map((rule) => (
                        <div key={rule.id} className={`bg-background-100 rounded-xl border border-background-200/70 p-6 hover:border-${rule.color}-200 transition-colors`}>
                          <div className="flex items-start gap-4">
                            <div className={`w-14 h-14 rounded-xl bg-${rule.color}-50 flex items-center justify-center flex-shrink-0`}>
                              <i className={`${rule.icon} text-2xl text-${rule.color}-500`}></i>
                            </div>
                            <div className="flex-1">
                              <div className="font-bold text-base text-foreground-950 mb-2">{rule.title}</div>
                              <p className="text-sm text-foreground-600 leading-relaxed mb-3">{rule.description}</p>
                              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-${rule.color}-50 text-xs font-semibold text-${rule.color}-600`}>
                                <i className="ri-flashlight-line text-xs"></i>
                                {rule.trigger}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Channel summary with rules context */}
                    <div className="bg-background-100 rounded-xl border border-background-200/70 p-6">
                      <div className="text-sm font-semibold text-foreground-950 mb-4">Los 5 Canales Oficiales</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {channelTypeConfigs.map((ch) => (
                          <div key={ch.id} className="bg-background-50 rounded-lg p-4 border border-background-200/70">
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${ch.gradient} flex items-center justify-center`}>
                                <i className={`${ch.icon} text-sm text-white`}></i>
                              </div>
                              <div>
                                <div className="font-semibold text-xs text-foreground-950">{ch.label}</div>
                                <div className={`text-[10px] ${ch.textColor}`}>{getContentByChannel(ch.id).length} contenidos</div>
                              </div>
                            </div>
                            <p className="text-[10px] text-foreground-500 leading-relaxed">{ch.triggerRule}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Cross-links section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-6">
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-1">Explorá más de RFW</p>
              <h4 className="text-lg font-bold text-foreground-950">Seguí descubriendo</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
              {[
                { label: 'Actividad Global', path: '/actividad-global', icon: 'ri-global-line', color: 'teal' },
                { label: 'Rankings', path: '/rankings', icon: 'ri-trophy-line', color: 'amber' },
                { label: 'Aniversarios', path: '/aniversarios', icon: 'ri-cake-line', color: 'rose' },
                { label: 'Rachas', path: '/rachas', icon: 'ri-fire-line', color: 'orange' },
                { label: 'Bienvenida', path: '/bienvenida', icon: 'ri-user-heart-line', color: 'emerald' },
                { label: 'Centro Social', path: '/centro-social', icon: 'ri-group-line', color: 'violet' },
                { label: 'Explorar', path: '/explorar', icon: 'ri-compass-3-line', color: 'sky' },
                { label: 'Eventos', path: '/eventos', icon: 'ri-calendar-event-line', color: 'fuchsia' },
                { label: 'Clubes', path: '/clubes', icon: 'ri-team-line', color: 'cyan' },
                { label: 'Mapa RFW', path: '/mapa-rfw', icon: 'ri-map-pin-line', color: 'emerald' },
                { label: 'Gamificación', path: '/gamificacion', icon: 'ri-gamepad-line', color: 'amber' },
                { label: 'Favoritos', path: '/favoritos', icon: 'ri-heart-line', color: 'rose' },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm font-medium text-foreground-600 hover:border-${link.color}-200 hover:text-${link.color}-600 transition-all cursor-pointer whitespace-nowrap`}
                >
                  <i className={`${link.icon} text-sm`}></i>
                  <span className="text-xs">{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Final Rule Banner */}
        <section className="py-8 bg-gradient-to-r from-rose-500 via-violet-500 to-amber-500">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <i className="ri-radio-line text-3xl text-white/80 mb-3 block"></i>
            <p className="text-white text-lg md:text-xl font-semibold mb-2">
              "La plataforma nunca debe sentirse vacía."
            </p>
            <p className="text-white/70 text-sm">
              Si una frecuencia no está activa → RFW Radio. Si no hay video → RFW TV. Siempre hay contenido oficial para vos.
            </p>
          </div>
        </section>
      </main>

      <Footer />

      {/* Spacers */}
      <div className="md:hidden h-32"></div>
      <div className="hidden md:block h-16"></div>
    </div>
  );
}